import {
  AdjustmentsHorizontalIcon,
  ArrowsUpDownIcon,
  CheckIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  Squares2X2Icon,
  TableCellsIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { api, type TaskScope } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys, useEpics, useSprints, useTasks } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { Button, ErrorState, PageSpinner, Tag } from "../ui";
import { Modal } from "../ui/Modal";
import { MenuDivider, MenuItem, MenuLabel, Popover } from "../ui/Popover";
import { Board } from "./Board";
import { useOpenTask } from "./TaskPeek";
import { TaskTable } from "./TaskTable";
import { applyFilters, applySort, filterFields, NONE, sortFields, useViewState, type FieldDef, type ViewState } from "./viewState";

function FilterChip({ field, values, onChange, onRemove }: { field: FieldDef; values: string[]; onChange: (v: string[]) => void; onRemove: () => void }) {
  const label = values.length === 0 ? "any" : values.length === 1 ? (field.choices.find((c) => c.id === values[0])?.label ?? "1 selected") : `${values.length} selected`;
  return (
    <Popover
      className="w-64 p-1"
      trigger={(t) => (
        <button
          ref={t.ref}
          onClick={t.onClick}
          type="button"
          className={cn("flex h-7 items-center gap-1 rounded-full border px-2.5 text-[13px]", values.length ? "border-accent/50 bg-accent/10 text-accent" : "border-line text-muted")}
        >
          <span>{field.label}:</span>
          <span className="max-w-32 truncate font-medium" dir="auto">
            {label}
          </span>
        </button>
      )}
    >
      {() => (
        <>
          <MenuLabel>{field.label} is any of</MenuLabel>
          <div className="max-h-72 overflow-y-auto scroll-thin">
            {field.choices.map((c) => {
              const on = values.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onChange(on ? values.filter((v) => v !== c.id) : [...values, c.id])}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm hover:bg-hover"
                >
                  <span className={cn("flex size-4 items-center justify-center rounded border", on ? "border-accent bg-accent text-white" : "border-line")}>
                    {on && <CheckIcon className="size-3" />}
                  </span>
                  {c.color ? <Tag color={c.color}>{c.label}</Tag> : <span className={cn("truncate", c.id === NONE && "text-muted")} dir="auto">{c.label}</span>}
                </button>
              );
            })}
          </div>
          <MenuDivider />
          <MenuItem icon={XMarkIcon} onClick={onRemove}>
            Remove filter
          </MenuItem>
        </>
      )}
    </Popover>
  );
}

function Toolbar({
  state,
  update,
  fields,
  count,
  onNew,
}: {
  state: ViewState;
  update: (p: Partial<ViewState>) => void;
  fields: FieldDef[];
  count: number;
  onNew?: () => void;
}) {
  const ctx = useProject();
  const groupable = ctx.propsFor("task").filter((p) => p.type !== "date");
  const sorts = sortFields(ctx);
  const unused = fields.filter((f) => !state.filters.some((x) => x.field === f.id));

  return (
    <div className="space-y-2 px-6 pb-3 md:px-12">
      <div className="flex flex-wrap items-center gap-1.5">
        <div className="flex items-center rounded-lg bg-hover p-0.5">
          {(
            [
              ["board", "Board", Squares2X2Icon],
              ["table", "Table", TableCellsIcon],
            ] as const
          ).map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              onClick={() => update({ view: id })}
              className={cn("flex h-7 items-center gap-1.5 rounded-md px-2.5 text-sm", state.view === id ? "bg-canvas font-medium shadow-sm" : "text-muted hover:text-fg")}
            >
              <Icon className="size-4" /> {label}
            </button>
          ))}
        </div>
        <div className="relative ms-1 min-w-40 flex-1 sm:max-w-64">
          <MagnifyingGlassIcon className="pointer-events-none absolute start-2 top-1/2 size-4 -translate-y-1/2 text-faint" />
          <input
            value={state.search}
            onChange={(e) => update({ search: e.target.value })}
            placeholder="Search tasks by name or number"
            className="field h-8 ps-7"
            dir="auto"
          />
        </div>
        <div className="ms-auto flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => update({ mine: !state.mine })}
            className={cn("flex h-8 items-center gap-1.5 rounded-md px-2 text-sm", state.mine ? "bg-accent/10 text-accent" : "text-muted hover:bg-hover")}
          >
            <UserIcon className="size-4" /> My tasks
          </button>
          <Popover
            className="w-56 p-1"
            placement="bottom-end"
            trigger={(t) => (
              <Button ref={t.ref} onClick={t.onClick} variant="ghost" icon={FunnelIcon}>
                Filter
              </Button>
            )}
          >
            {(close) => (
              <>
                <MenuLabel>Add a filter</MenuLabel>
                {unused.map((f) => (
                  <MenuItem key={f.id} onClick={() => (close(), update({ filters: [...state.filters, { field: f.id, values: [] }] }))}>
                    {f.label}
                  </MenuItem>
                ))}
                {unused.length === 0 && <div className="px-2 py-1.5 text-sm text-faint">All filters added</div>}
              </>
            )}
          </Popover>
          <Popover
            className="w-56 p-1"
            placement="bottom-end"
            trigger={(t) => (
              <Button ref={t.ref} onClick={t.onClick} variant="ghost" icon={ArrowsUpDownIcon}>
                Sort
              </Button>
            )}
          >
            {() => (
              <>
                <MenuLabel>Sort by</MenuLabel>
                {sorts.map((s) => (
                  <MenuItem key={s.id} active={state.sort.key === s.id} onClick={() => update({ sort: { key: s.id, dir: state.sort.key === s.id ? state.sort.dir : "asc" } })} right={state.sort.key === s.id ? <CheckIcon className="size-4" /> : undefined}>
                    {s.label}
                  </MenuItem>
                ))}
                {state.sort.key !== "manual" && (
                  <>
                    <MenuDivider />
                    <MenuItem onClick={() => update({ sort: { ...state.sort, dir: state.sort.dir === "asc" ? "desc" : "asc" } })}>
                      {state.sort.dir === "asc" ? "Ascending ↑" : "Descending ↓"}
                    </MenuItem>
                  </>
                )}
              </>
            )}
          </Popover>
          {state.view === "board" && (
            <Popover
              className="w-60 p-1"
              placement="bottom-end"
              trigger={(t) => (
                <Button ref={t.ref} onClick={t.onClick} variant="ghost" icon={AdjustmentsHorizontalIcon}>
                  Group
                </Button>
              )}
            >
              {() => (
                <>
                  <MenuLabel>Group columns by</MenuLabel>
                  <MenuItem active={state.groupBy === "stage"} onClick={() => update({ groupBy: "stage" })} right={state.groupBy === "stage" ? <CheckIcon className="size-4" /> : undefined}>
                    Stage
                  </MenuItem>
                  {groupable.map((p) => (
                    <MenuItem key={p.id} active={state.groupBy === p.id} onClick={() => update({ groupBy: p.id })} right={state.groupBy === p.id ? <CheckIcon className="size-4" /> : undefined}>
                      {p.name}
                    </MenuItem>
                  ))}
                  <MenuDivider />
                  <MenuItem onClick={() => update({ hideEmpty: !state.hideEmpty })} right={state.hideEmpty ? <CheckIcon className="size-4" /> : undefined}>
                    Hide empty columns
                  </MenuItem>
                </>
              )}
            </Popover>
          )}
          {onNew && ctx.canEdit && (
            <Button variant="primary" icon={PlusIcon} onClick={onNew}>
              New
            </Button>
          )}
        </div>
      </div>
      {(state.filters.length > 0 || state.mine || state.search) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {state.filters.map((f, i) => {
            const field = fields.find((x) => x.id === f.field);
            if (!field) return null;
            return (
              <FilterChip
                key={f.field}
                field={field}
                values={f.values}
                onChange={(values) => update({ filters: state.filters.map((x, j) => (j === i ? { ...x, values } : x)) })}
                onRemove={() => update({ filters: state.filters.filter((_, j) => j !== i) })}
              />
            );
          })}
          <span className="text-xs text-muted">{count} tasks</span>
          <button type="button" onClick={() => update({ filters: [], mine: false, search: "" })} className="text-xs text-muted hover:text-fg hover:underline">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

function NewTaskDialog({ open, onClose, stageId }: { open: boolean; onClose: () => void; stageId?: string }) {
  const { pid, bundle } = useProject();
  const epics = useEpics(pid);
  const [epicId, setEpicId] = useState(() => localStorage.getItem(`last-epic:${pid}`) ?? "");
  const sprints = useSprints(pid, epicId || undefined);
  const [sprintId, setSprintId] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const openTask = useOpenTask();
  const qc = useQueryClient();
  const validEpic = epics.data?.some((e) => e.id === epicId) ? epicId : (epics.data?.[0]?.id ?? "");

  const create = async () => {
    if (!title.trim() || !validEpic) return;
    setSaving(true);
    try {
      const t = await api.createTask(pid, { epicId: validEpic, sprintId: sprintId || null, title: title.trim(), stageId });
      localStorage.setItem(`last-epic:${pid}`, validEpic);
      await qc.invalidateQueries({ queryKey: keys.project(pid) });
      setTitle("");
      onClose();
      openTask(t.id);
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New task"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={create} loading={saving} disabled={!title.trim() || !validEpic}>
            Create task
          </Button>
        </>
      }
    >
      {epics.data?.length === 0 ? (
        <p className="text-sm text-muted">Tasks belong to an epic. Create an epic first from the sidebar or the project overview.</p>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            create();
          }}
        >
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" className="field text-base" dir="auto" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-[13px] text-muted">Epic</span>
              <select
                value={validEpic}
                onChange={(e) => {
                  setEpicId(e.target.value);
                  setSprintId("");
                }}
                className="field"
              >
                {epics.data?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-[13px] text-muted">Sprint</span>
              <select value={sprintId} onChange={(e) => setSprintId(e.target.value)} className="field">
                <option value="">Backlog (no sprint)</option>
                {sprints.data
                  ?.filter((s) => s.epicId === validEpic)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          {stageId && <p className="text-xs text-faint">Stage: {bundle.stages.find((s) => s.id === stageId)?.name}</p>}
        </form>
      )}
    </Modal>
  );
}

/**
 * Board + table for a set of tasks. `scope` selects the tasks; `createIn` enables inline creation
 * when the target epic (and sprint) is known, otherwise a dialog asks for the epic.
 */
export function TaskViews({ scope, storageKey, createIn }: { scope: TaskScope; storageKey: string; createIn?: { epicId: string; sprintId: string | null } }) {
  const ctx = useProject();
  const { pid, me } = ctx;
  const [state, update] = useViewState(storageKey);
  const tasks = useTasks(pid, scope);
  const projectScope = !scope.sprintId && !scope.epicId;
  const epics = useEpics(pid);
  const sprints = useSprints(pid);
  const openTask = useOpenTask();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ stageId?: string } | null>(null);

  const fields = useMemo(() => filterFields(ctx, { epics: epics.data, sprints: sprints.data, projectScope }), [ctx, epics.data, sprints.data, projectScope]);
  const visible = useMemo(() => applySort(applyFilters(tasks.data ?? [], state, me.id), state, ctx), [tasks.data, state, me.id, ctx]);
  const epicTitles = useMemo(() => (projectScope ? new Map(epics.data?.map((e) => [e.id, e.title])) : undefined), [projectScope, epics.data]);

  const createTask = async (title: string, groupId?: string) => {
    if (!createIn) return;
    const groupProp = ctx.bundle.properties.find((p) => p.id === state.groupBy);
    const stageId = !groupProp && groupId ? groupId : undefined;
    try {
      const t = await api.createTask(pid, { ...createIn, title, stageId });
      if (groupProp && groupId && groupId !== NONE) await api.setValue(pid, t.id, groupProp.id, [groupId]);
      await qc.invalidateQueries({ queryKey: keys.project(pid) });
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
      throw err;
    }
  };

  const onSort = (key: string) =>
    update({ sort: state.sort.key === key ? { key, dir: state.sort.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" } });

  return (
    <div>
      <Toolbar state={state} update={update} fields={fields} count={visible.length} onNew={() => setDialog({})} />
      {tasks.isPending ? (
        <PageSpinner />
      ) : tasks.error ? (
        <ErrorState message="Couldn't load tasks" onRetry={() => tasks.refetch()} />
      ) : state.view === "board" ? (
        <Board
          tasks={visible}
          state={state}
          onOpen={openTask}
          epicTitles={epicTitles}
          onCreate={createIn ? (groupId, title) => createTask(title, groupId) : undefined}
          onAddClick={(groupId) => setDialog({ stageId: ctx.bundle.properties.some((p) => p.id === state.groupBy) ? undefined : groupId })}
        />
      ) : (
        <TaskTable
          tasks={visible}
          state={state}
          onSort={onSort}
          onOpen={openTask}
          epics={epics.data}
          sprints={sprints.data}
          projectScope={projectScope}
          onCreate={createIn ? (title) => createTask(title) : undefined}
        />
      )}
      {dialog && <NewTaskDialog open onClose={() => setDialog(null)} stageId={dialog.stageId} key={JSON.stringify(dialog)} />}
    </div>
  );
}
